/**
 * WebSocket connection management
 */

import React, { createContext, useContext, useEffect, useRef, useCallback, useMemo, useState } from "react";
import { decode } from "@msgpack/msgpack";
import { useViewerStore } from "./store";
import { dispatchInboundMessage, type MessageHandlerContext } from "./messageHandlers";
import { createSenders, type Senders } from "./webSocketSenders";
import { useUrdfExport } from "./urdfExport";
import type {
  Message,
  ExportUrdfResultMessage,
  ExportUrdfProgressMessage,
  UrdfForConfigResultMessage,
  UploadModuleResultMessage,
  SaveConfigResultMessage,
  SaveConnectableLinksResultMessage,
  RenameJointResultMessage,
  JointGroupResultMessage,
  JointSettingsResultMessage,
  MeshFormat,
} from "./types";

interface WebSocketContextValue extends Senders {
  isConnected: boolean;
  send: (message: Message) => void;
  // Stays out of createSenders: it also reads the export callback ref and the
  // cross-provider "expects callback" flag, both of which live in urdfExport.ts.
  sendExportUrdf: (
    packageName: string,
    visualMeshFormat?: MeshFormat,
    collisionMeshFormat?: MeshFormat
  ) => void;
  // Callback for urdf_for_config_result
  onUrdfForConfigResult: ((result: UrdfForConfigResultMessage) => void) | null;
  setOnUrdfForConfigResult: (callback: ((result: UrdfForConfigResultMessage) => void) | null) => void;
  // Callback for upload_module_result
  onUploadModuleResult: ((result: UploadModuleResultMessage) => void) | null;
  setOnUploadModuleResult: (callback: ((result: UploadModuleResultMessage) => void) | null) => void;
  // Callback for export_urdf_result (used by Config Generator)
  onExportUrdfResult: ((result: ExportUrdfResultMessage) => void) | null;
  setOnExportUrdfResult: (callback: ((result: ExportUrdfResultMessage) => void) | null) => void;
  setOnExportUrdfProgress: (callback: ((progress: ExportUrdfProgressMessage) => void) | null) => void;
}

const WebSocketContext = createContext<WebSocketContextValue | null>(null);

export function useWebSocket(): WebSocketContextValue {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error("useWebSocket must be used within WebSocketProvider");
  }
  return context;
}

interface WebSocketProviderProps {
  url: string;
  children: React.ReactNode;
}

// Reconnection parameters
const RECONNECT_BASE_DELAY_MS = 1000;
const RECONNECT_MAX_DELAY_MS = 30000;
const HEARTBEAT_INTERVAL_MS = 30000;

export function WebSocketProvider({ url, children }: WebSocketProviderProps) {
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<number | null>(null);
  const reconnectAttemptRef = useRef(0);
  const heartbeatIntervalRef = useRef<number | null>(null);
  const urdfForConfigCallbackRef = useRef<((result: UrdfForConfigResultMessage) => void) | null>(null);
  const uploadModuleCallbackRef = useRef<((result: UploadModuleResultMessage) => void) | null>(null);
  const [isConnected, setIsConnected] = useState(false);

  const store = useViewerStore;

  const connect = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      return;
    }

    const ws = new WebSocket(url);
    wsRef.current = ws;

    ws.onopen = () => {
      console.log("WebSocket connected");
      setIsConnected(true);
      store.getState().setConnected(true);
      reconnectAttemptRef.current = 0;

      // Clear reconnect timeout
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }

      // Start heartbeat ping
      if (heartbeatIntervalRef.current) {
        clearInterval(heartbeatIntervalRef.current);
      }
      heartbeatIntervalRef.current = window.setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({ type: "ping" }));
        }
      }, HEARTBEAT_INTERVAL_MS);
    };

    ws.onclose = (event) => {
      console.log("WebSocket disconnected", event.code, event.reason);
      setIsConnected(false);
      store.getState().setConnected(false);

      // Stop heartbeat
      if (heartbeatIntervalRef.current) {
        clearInterval(heartbeatIntervalRef.current);
        heartbeatIntervalRef.current = null;
      }

      // Session belongs to another user — redirect to a new session
      if (event.code === 4403) {
        console.warn("Session access denied:", event.reason);
        window.location.href = "/";
        return;
      }

      // Exponential backoff with jitter: base * 2^attempt + random jitter
      const attempt = reconnectAttemptRef.current;
      const baseDelay = Math.min(
        RECONNECT_BASE_DELAY_MS * Math.pow(2, attempt),
        RECONNECT_MAX_DELAY_MS,
      );
      const jitter = Math.random() * RECONNECT_BASE_DELAY_MS;
      const delay = baseDelay + jitter;
      reconnectAttemptRef.current = attempt + 1;

      console.log(`Reconnecting in ${Math.round(delay)}ms (attempt ${attempt + 1})...`);
      reconnectTimeoutRef.current = window.setTimeout(() => {
        connect();
      }, delay);
    };

    ws.onerror = (error) => {
      console.error("WebSocket error:", error);
    };

    // Use arraybuffer for binary messages (MessagePack)
    ws.binaryType = "arraybuffer";

    ws.onmessage = (event) => {
      try {
        let message: Message;

        if (event.data instanceof ArrayBuffer) {
          // Binary message: decode with MessagePack
          message = decode(new Uint8Array(event.data)) as Message;
        } else {
          // Text message: parse as JSON
          message = JSON.parse(event.data) as Message;
        }

        handleMessage(message);
      } catch (error) {
        console.error("Failed to parse message:", error);
      }
    };
  }, [url]);

  const handleMessage = (message: Message) => {
    const context: MessageHandlerContext = {
      state: store.getState(),
      handleExportResult,
      handleExportProgress,
      handleSaveConfigResult,
      handleSaveConnectableLinksResult,
      handleRenameJointResult,
      handleJointGroupResult,
      handleJointSettingsResult,
      urdfForConfigCallbackRef,
      uploadModuleCallbackRef,
    };

    if (!dispatchInboundMessage(message, context)) {
      console.warn("Unknown message type:", message);
    }
  };

  const handleSaveConfigResult = (message: SaveConfigResultMessage) => {
    if (message.success && message.data && message.filename) {
      // Create JSON blob and trigger download
      const blob = new Blob([message.data], { type: "application/json" });

      // Create download link
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = message.filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      console.log(`Downloaded: ${message.filename}`);
    } else if (message.error) {
      console.error(`Save config failed: ${message.error}`);
      alert(`Save config failed: ${message.error}`);
    }
  };

  const handleSaveConnectableLinksResult = (message: SaveConnectableLinksResultMessage) => {
    if (message.success) {
      console.log("Connectable links saved successfully");
    } else if (message.error) {
      console.error(`Save connectable links failed: ${message.error}`);
      alert(`Save connectable links failed: ${message.error}`);
    }
  };

  const handleRenameJointResult = (message: RenameJointResultMessage) => {
    if (message.success) {
      console.log(`Joint renamed: ${message.joint_name} -> ${message.new_name}`);
    } else if (message.error) {
      console.error(`Joint rename failed: ${message.error}`);
      alert(`Joint rename failed: ${message.error}`);
    }
  };

  const handleJointGroupResult = (message: JointGroupResultMessage) => {
    if (message.success) {
      console.log(`Joint group ${message.operation}: ${message.group_name}`);
    } else if (message.error) {
      console.error(`Joint group ${message.operation} failed: ${message.error}`);
      alert(`Joint group ${message.operation} failed: ${message.error}`);
    }
  };

  const handleJointSettingsResult = (message: JointSettingsResultMessage) => {
    if (message.success) {
      console.log(`Joint settings updated: ${message.joint_name}`);
    } else if (message.error) {
      console.error(`Joint settings update failed: ${message.error}`);
      alert(`Joint settings update failed: ${message.error}`);
    }
  };

  useEffect(() => {
    connect();

    return () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (heartbeatIntervalRef.current) {
        clearInterval(heartbeatIntervalRef.current);
      }
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, [connect]);

  const send = useCallback((message: Message) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      console.log("[WebSocket] Sending message:", message.type, message);
      wsRef.current.send(JSON.stringify(message));
    } else {
      console.error("[WebSocket] Cannot send - connection not open. State:", wsRef.current?.readyState, "Message:", message.type);
    }
  }, []);

  const senders = useMemo(() => createSenders(send), [send]);

  const {
    sendExportUrdf,
    setOnExportUrdfResult,
    setOnExportUrdfProgress,
    handleExportResult,
    handleExportProgress,
    onExportUrdfResult,
  } = useUrdfExport(send);

  const setOnUrdfForConfigResult = useCallback(
    (callback: ((result: UrdfForConfigResultMessage) => void) | null) => {
      urdfForConfigCallbackRef.current = callback;
    },
    []
  );

  const setOnUploadModuleResult = useCallback(
    (callback: ((result: UploadModuleResultMessage) => void) | null) => {
      uploadModuleCallbackRef.current = callback;
    },
    []
  );

  const contextValue: WebSocketContextValue = {
    isConnected,
    send,
    ...senders,
    sendExportUrdf,
    onUrdfForConfigResult: urdfForConfigCallbackRef.current,
    setOnUrdfForConfigResult,
    onUploadModuleResult: uploadModuleCallbackRef.current,
    setOnUploadModuleResult,
    onExportUrdfResult,
    setOnExportUrdfResult,
    setOnExportUrdfProgress,
  };

  return (
    <WebSocketContext.Provider value={contextValue}>{children}</WebSocketContext.Provider>
  );
}
